import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { StickyContainer } from 'react-sticky';
import { connectToStores } from 'fluxible-addons-react';
import { partial } from 'lodash';
import cx from 'classnames';
import FileSaver from 'file-saver';
import ApplicationStore from '../../../stores/ApplicationStore';
import QuickstartStore from '../../../stores/QuickstartStore';
import UserStore from '../../../stores/UserStore';
import { login } from '../../../action/navigateAuthentication';
import {
  sendTutorialViewedEvent,
  sendPackageDownloadEvent,
  sendViewOnGithubEvent
} from '../../../browser/quickstartMetrics';
import Sidebar from '../../Sidebar';
import NavigationBar from '../../NavigationBar';
import TryBanner from '../../TryBanner';
import ArticleFooter from '../../ArticleFooter';
import Breadcrumbs from '../../Breadcrumbs';
import Tutorial from '../../quickstarts/Tutorial';
import TutorialVersionSelector from '../../quickstarts/TutorialVersionSelector';
import setInnerHtmlHooks from '../../../browser/setInnerHtmlHooks';
import Author from '../../quickstarts/Author';
import StickyHeader from '../../quickstarts/StickyHeader';
import DownloadBox from '../../quickstarts/DownloadBox';
import DownloadSampleModal from '../../quickstarts/DownloadSampleModal';
import Requirements from '../../quickstarts/Requirements';
import Introduction from '../../quickstarts/Introduction';
import styles from './styles.module.styl';
import replaceUserVars from '../../../../lib/replaceUserVars';
import getSampleRequiredUserResources from './getSampleRequiredUserResources';

// eslint-disable-next-line no-confusing-arrow
const getTitle = (platform, article) =>
  platform.articles.length === 1 ?
    platform.title :
    `${platform.title}: ${article.title}`;

class TutorialPage extends React.Component {
  static propTypes = {
    quickstarts: PropTypes.object,
    quickstart: PropTypes.object,
    platform: PropTypes.object,
    version: PropTypes.object,
    article: PropTypes.object,
    userAccount: PropTypes.object,
    selectedApi: PropTypes.object,
    isAuthenticated: PropTypes.bool,
    currentRoute: PropTypes.object.isRequired,
    isFramedMode: PropTypes.bool.isRequired,
    domainUrlApp: PropTypes.string.isRequired,
    domainUrlDocs: PropTypes.string.isRequired,
    breadcrumbs: PropTypes.array.isRequired,
    sidebarItems: PropTypes.array.isRequired,
    sidebarBreadcrumbs: PropTypes.array.isRequired,
    metadataNextSteps: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool
    ]),
    sidebarArticlesNextSteps: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool
    ])
  };

  static contextTypes = {
    getStore: PropTypes.func,
    executeAction: PropTypes.func,
    trackEvent: PropTypes.func.isRequired
  };

  state = {
    showDownloadModal: false,
    downloadInProgress: false
  }

  componentDidMount() {
    this.handleMetrics(this.props.article);
  }

  componentDidUpdate(prevProps) {
    this.handleMetrics(this.props.article, prevProps.article);
  }

  setDownloadModalVisibility = (visible) => {
    this.setState({ showDownloadModal: visible });
  }
  showDownloadModal = partial(this.setDownloadModalVisibility, true)
  hideDownloadModal = partial(this.setDownloadModalVisibility, false)

  handleContentRender = (elem) => {
    setInnerHtmlHooks(elem, this.props.isFramedMode);
  }

  handleLogin = () => {
    this.context.executeAction(login);
  }

  downloadSample = () => {
    const {
      domainUrlDocs,
      article,
      platform,
      userAccount,
      selectedApi
    } = this.props;

    this.setState({ downloadInProgress: true });

    fetch(`${domainUrlDocs}/package/v2`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        ...article.githubData,
        client_id: userAccount.clientId,
        client_secret: userAccount.clientSecret,
        callback_url: userAccount.callback,
        domain: userAccount.namespace,
        tenant: userAccount.tenant,
        api_id: selectedApi && selectedApi.identifier
      })
    }).then((response) => {
      if (!response.ok) {
        return false;
      }

      return response.blob();
    }).then((blob) => {
      if (blob) {
        FileSaver.saveAs(blob, `${platform.name}-${article.name}.zip`);
        sendPackageDownloadEvent(this.props);
      }
    }).finally(() => {
      this.setState({ downloadInProgress: false });
    });
  };

  sendViewOnGithubEvent = () => {
    sendViewOnGithubEvent(this.props);
  }

  handleMetrics(article, prevArticle) {
    if (typeof document === 'undefined') return;
    if (article && (!prevArticle || prevArticle.url !== article.url)) {
      sendTutorialViewedEvent(this.props);
    }
  }

  render() {
    const {
      quickstarts,
      quickstart,
      platform,
      article,
      version,
      userAccount,
      isFramedMode,
      domainUrlApp,
      breadcrumbs,
      sidebarItems,
      sidebarBreadcrumbs,
      metadataNextSteps,
      sidebarArticlesNextSteps,
      isAuthenticated,
      selectedApi
    } = this.props;

    if (!platform || !article) {
      return <span>Loading</span>;
    }

    const title = getTitle(platform, article);
    const showSidebar = platform.articles.length > 1 && !isFramedMode;

    const sampleRequiredUserResources = article.showDownloadSample && (
      getSampleRequiredUserResources(
        (userAccount.clientId !== 'YOUR_CLIENT_ID') && userAccount.clientId,
        selectedApi.identifier,
        article.sampleDownloadRequiredData,
        domainUrlApp
      )
    );

    return (
      <div className={cx('docs-quickstart', { 'doc-with-sidebar': showSidebar })}>
        <div id="tutorial-template" className="docs-single animated fadeIn">
          {!isFramedMode && <NavigationBar currentSection="quickstarts" />}
          <StickyContainer>
            <StickyHeader
              showSampleButtons={article.showDownloadSample}
              isAuthenticated={isAuthenticated}
              requiredResources={sampleRequiredUserResources}
              title={title}
              showDownloadModal={this.showDownloadModal}
              githubSampleLink={article.githubSampleLink}
              trackViewGithubSample={this.sendViewOnGithubEvent}
              handleLogin={this.handleLogin}
            />
            <div className="js-doc-template tutorial-page container">
              <div className={cx('row', styles.tutorialRow)}>
                <div className={`col-md-${isFramedMode ? 12 : 8}`}>
                  <div className={styles.breadcrumbContainer}>
                    <Breadcrumbs crumbs={breadcrumbs} />
                  </div>
                  <section>
                    <article data-swiftype-index="true" className="docs-content">
                      <h1 className="tutorial-title">{title}</h1>
                      <Author {...platform.author} />
                      <TutorialVersionSelector
                        quickstarts={quickstarts}
                        quickstart={quickstart}
                        platform={platform}
                        version={version}
                        article={article}
                      />
                      <Introduction
                        text={article.description}
                        showLoginText={!isAuthenticated}
                        handleLogin={this.handleLogin}
                      />
                      { article.showDownloadSample &&
                        <Fragment>
                          {!sampleRequiredUserResources &&
                            <DownloadSampleModal
                              show={this.state.showDownloadModal}
                              handleHide={this.hideDownloadModal}
                              showClient={article.sampleDownloadRequiredData.includes('client')}
                              showApi={article.sampleDownloadRequiredData.includes('api')}
                              selectedAppName={userAccount.appName}
                              selectedApi={selectedApi}
                              downloadSample={this.downloadSample}
                              githubSampleLink={article.githubSampleLink}
                              trackViewGithubSample={this.sendViewOnGithubEvent}
                              downloadInstructions={article.downloadInstructions}
                              downloadInProgress={this.state.downloadInProgress}
                              isFramedMode={isFramedMode}
                            />
                          }
                          <DownloadBox
                            isAuthenticated={isAuthenticated}
                            requiredResources={sampleRequiredUserResources}
                            showSteps={article.showSteps}
                            steps={article.titles.filter(t => t.level === 2)}
                            description={article.downloadBoxDescription}
                            showDownloadModal={this.showDownloadModal}
                            githubSampleLink={article.githubSampleLink}
                            trackViewGithubSample={this.sendViewOnGithubEvent}
                            handleLogin={this.handleLogin}
                          />
                        </Fragment>
                      }
                      {article.showDownloadSample && article.requirements &&
                        <Requirements list={article.requirements} />
                      }
                      <div data-swiftype-name="body" data-swiftype-type="text">
                        <Tutorial
                          quickstart={quickstart}
                          platform={platform}
                          article={article}
                          handleRender={this.handleContentRender}
                        />
                      </div>
                      <div data-swiftype-index="false">
                        <ArticleFooter
                          articleUrl={article.url}
                          githubEditLink={article.githubEditLink}
                          platformTitle={platform.title}
                          metadataNextSteps={metadataNextSteps}
                          sidebarArticlesNextSteps={sidebarArticlesNextSteps}
                          isQuickstart
                        />
                      </div>
                    </article>
                  </section>
                  {!isAuthenticated && !isFramedMode && <TryBanner />}
                </div>
                { showSidebar && (
                  <div className={cx(
                    styles.sidebarContainer, 'sidebar-container', 'col-md-3', 'col-md-offset-1'
                  )}
                  >
                    <Sidebar
                      section="Tutorials"
                      items={sidebarItems}
                      breadcrumbs={sidebarBreadcrumbs}
                      url={this.props.currentRoute.url}
                      isQuickstart
                    />
                  </div>
                )}
              </div>
            </div>
          </StickyContainer>
        </div>
      </div>
    );
  }
}

const replaceVarsInDownloadInstructions = (article, context) => {
  if (!article || !article.downloadInstructions) return article;

  return {
    ...article,
    downloadInstructions: replaceUserVars(article.downloadInstructions, context)
  };
};

export default connectToStores(
  TutorialPage,
  [ApplicationStore, QuickstartStore, UserStore],
  (context) => {
    const appStore = context.getStore(ApplicationStore);
    const quickstartStore = context.getStore(QuickstartStore);
    const userStore = context.getStore(UserStore);

    const article = quickstartStore.getCurrentArticle();
    const account = userStore.getUserAccount();
    const selectedApi = userStore.getSelectedApi();

    return {
      quickstarts: quickstartStore.getQuickstarts(),
      quickstart: quickstartStore.getCurrentQuickstart(),
      platform: quickstartStore.getCurrentPlatform(),
      version: quickstartStore.getCurrentVersion(),
      sidebarItems: quickstartStore.getSidebarItems(),
      article: replaceVarsInDownloadInstructions(article, { account, apiIdentifier: selectedApi.identifier }),
      userAccount: account,
      selectedApi,
      isFramedMode: appStore.isFramedMode(),
      domainUrlApp: appStore.getDomainUrlApp(),
      domainUrlDocs: appStore.getDomainUrlDocs(),
      isAuthenticated: userStore.isAuthenticated(),
      breadcrumbs: quickstartStore.getBreadcrumbs(),
      sidebarBreadcrumbs: quickstartStore.getSidebarBreadcrumbs(),
      metadataNextSteps: quickstartStore.getMetadataNextSteps(),
      sidebarArticlesNextSteps: quickstartStore.getSidebarArticlesNextSteps()
    };
  }
);
